/* eslint-disable @typescript-eslint/no-explicit-any */
import { Modal, Progress, Typography, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback, useRef } from 'react';
import IntlMessages from '@/components/Locale/IntlMessages';
import type { InitProgress } from '@/types/global-config';
import { useRouter } from 'next/router';
import { getPhanHeFromStorage, resolvePhanHeFromRoute } from '@/utils/phanHeStorage';
import { CONSTANTS } from '@/utils/constants';

const { Text } = Typography;

/** Simple reactive store for init progress - attached to window */
class InitProgressStore {
    private _progress: InitProgress = {
        phase: 'idle',
        message: '',
        percent: 0,
    };
    private _listeners = new Set<(p: InitProgress) => void>();
    private _modalOpen = false;
    private _modalListeners = new Set<(open: boolean) => void>();

    getProgress(): InitProgress {
        return this._progress;
    }

    setProgress(progress: Partial<InitProgress>) {
        this._progress = { ...this._progress, ...progress };
        this._listeners.forEach((l) => l(this._progress));
    }

    subscribe(listener: (p: InitProgress) => void): () => void {
        this._listeners.add(listener);
        return () => this._listeners.delete(listener);
    }

    isModalOpen(): boolean {
        return this._modalOpen;
    }

    setModalOpen(open: boolean) {
        this._modalOpen = open;
        this._modalListeners.forEach((l) => l(open));
    }

    subscribeModal(listener: (open: boolean) => void): () => void {
        this._modalListeners.add(listener);
        return () => this._modalListeners.delete(listener);
    }
}

/** Singleton instance */
const progressStore = new InitProgressStore();

// Attach to window for cross-module access
if (typeof window !== 'undefined') {
    (window as any).__initProgress = progressStore;
}

export { progressStore as initProgressStore };

export interface InitLoadingModalProps {
    /** Custom progress (optional - uses global store by default) */
    progress?: InitProgress;
    /** Callback when loading completes */
    onComplete?: () => void;
}

const PHASE_CONFIG = {
    idle: { percent: 0, color: '#d9d9d9' },
    critical: { percent: 80, color: '#1890ff' },
    done: { percent: 100, color: '#52c41a' },
};

export const InitLoadingModal: React.FC<InitLoadingModalProps> = ({
    progress: propProgress,
    onComplete,
}) => {
    const { t } = useTranslation();
    const router = useRouter();
    // Phân hệ Quy trình: đồng bộ cơ cấu chạy ngầm, không hiện modal (URL trước, route dùng chung thì theo storage).
    const isQuyTrinh =
        (resolvePhanHeFromRoute(router.pathname, router.asPath) ?? getPhanHeFromStorage()) ===
        CONSTANTS.PHAN_HE.QUY_TRINH;
    const [progress, setProgress] = useState<InitProgress>(propProgress ?? {
        phase: 'idle',
        message: '',
        percent: 0,
    });
    const [visible, setVisible] = useState(false);
    const [displayPercent, setDisplayPercent] = useState(0);
    const isFirstRender = useRef(true);
    const prevPhaseRef = useRef<InitProgress['phase']>('idle');

    // Subscribe to global progress store
    useEffect(() => {
        if (propProgress) return; // Use prop if provided
        
        const unsubscribe = progressStore.subscribe((p) => {
            setProgress(p);
        });
        return unsubscribe;
    }, [propProgress]);

    // Determine if modal should be visible
    useEffect(() => {
        if (progress.phase === 'done') {
            // Auto close after done
            const timer = setTimeout(() => {
                setVisible(false);
                onComplete?.();
            }, 800);
            return () => clearTimeout(timer);
        } else if (progress.phase === 'critical') {
            setVisible(true);
        }
    }, [progress.phase, onComplete]);

    // Smooth animation for progress
    useEffect(() => {
        const target = PHASE_CONFIG[progress.phase]?.percent ?? progress.percent;
        if (displayPercent < target) {
            const timer = setTimeout(() => {
                setDisplayPercent((prev) => Math.min(prev + 8, target));
            }, 30);
            return () => clearTimeout(timer);
        } else if (displayPercent > target) {
            setDisplayPercent(target);
        }
    }, [displayPercent, progress.phase, progress.percent]);

    // First render animation
    useEffect(() => {
        if (isFirstRender.current && visible) {
            isFirstRender.current = false;
            const timer = setTimeout(() => {
                setDisplayPercent(5);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [visible]);

    const config = PHASE_CONFIG[progress.phase] ?? PHASE_CONFIG.idle;

    if (!visible || isQuyTrinh) return null;

    return (
        <Modal
            open={visible}
            closable={false}
            footer={null}
            maskClosable={false}
            keyboard={false}
            centered
            width={420}
            className="init-loading-modal"
            styles={{
                body: { padding: '40px 32px', textAlign: 'center' },
                mask: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
            }}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Loading Icon */}
                <div style={{ fontSize: 56, lineHeight: 1, height: 56 }}>
                    {progress.phase === 'done' ? (
                        <span style={{ color: '#52c41a' }}>✓</span>
                    ) : progress.phase === 'critical' ? (
                        <span className="init-spinner" style={{ display: 'inline-block', animation: 'initSpin 1s linear infinite' }}>⟳</span>
                    ) : (
                        <span style={{ color: '#d9d9d9' }}>○</span>
                    )}
                </div>

                {/* Title */}
                <div>
                    <Text strong style={{ fontSize: 20, display: 'block', marginBottom: 8 }}>
                        <IntlMessages id="app.init.title" text="Khởi tạo dữ liệu" />
                    </Text>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                        {progress.phase === 'done' 
                            ? t('app.init.done', 'Đã hoàn tất!')
                            : t('app.init.loading', 'Đang tải dữ liệu cơ cấu...')}
                    </Text>
                </div>

                {/* Progress Bar */}
                <div style={{ width: '100%', padding: '0 16px' }}>
                    <Progress
                        percent={displayPercent}
                        status={progress.phase === 'done' ? 'success' : 'active'}
                        strokeColor={config.color}
                        showInfo={false}
                        size="small"
                    />
                </div>

                {/* Status Message */}
                <Text style={{ fontSize: 14, minHeight: 20 }}>
                    {progress.message || t('app.init.preparing', 'Đang chuẩn bị...')}
                </Text>

                {/* Phase indicators */}
                <Space size="large" style={{ marginTop: 8 }}>
                    <PhaseIndicator
                        label={t('app.init.phase.departments', 'Cơ cấu')}
                        status={getPhaseStatus(progress.phase, 0)}
                    />
                    <PhaseIndicator
                        label={t('app.init.phase.positions', 'Chức vụ')}
                        status={getPhaseStatus(progress.phase, 1)}
                    />
                    <PhaseIndicator
                        label={t('app.init.phase.employees', 'Nhân viên')}
                        status={getPhaseStatus(progress.phase, 2)}
                    />
                </Space>

                {/* Hint */}
                <Text type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
                    <IntlMessages id="app.init.hint" text="Vui lòng đợi trong giây lát..." />
                </Text>
            </Space>

            {/* CSS for spinner animation */}
            <style>{`
                @keyframes initSpin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Modal>
    );
};

type PhaseStatus = 'pending' | 'loading' | 'done';

function getPhaseStatus(currentPhase: InitProgress['phase'], phaseIndex: number): PhaseStatus {
    if (currentPhase === 'done') return 'done';
    if (currentPhase === 'critical' && phaseIndex === 0) return 'loading';
    if (currentPhase === 'critical') return 'pending';
    return 'pending';
}

interface PhaseIndicatorProps {
    label: string;
    status: PhaseStatus;
}

const PhaseIndicator: React.FC<PhaseIndicatorProps> = ({ label, status }) => {
    const getConfig = () => {
        switch (status) {
            case 'done':
                return { color: '#52c41a', bg: '#f6ffed', icon: '✓' };
            case 'loading':
                return { color: '#1890ff', bg: '#e6f7ff', icon: '...' };
            case 'pending':
            default:
                return { color: '#d9d9d9', bg: '#f5f5f5', icon: '○' };
        }
    };

    const config = getConfig();

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                padding: '8px 12px',
                borderRadius: 6,
                backgroundColor: config.bg,
                minWidth: 80,
            }}
        >
            <span style={{ color: config.color, fontWeight: 600, fontSize: 14 }}>
                {config.icon}
            </span>
            <Text
                style={{
                    fontSize: 11,
                    color: status === 'pending' ? '#bfbfbf' : '#595959',
                    fontWeight: status === 'loading' ? 600 : 400,
                    textAlign: 'center',
                }}
            >
                {label}
            </Text>
        </div>
    );
};
